import type { FetchOptions } from 'ofetch'

import type {
	ApiState,
	ApiSuccessResponse,
	ExtendedFetchOptions
} from '@/types/api-client'
import type {
	AssignPermissionPayload,
	CreatePermissionPayload,
	RemovePermissionPayload,
	SuperAdminPermission,
	UpdatePermissionPayload
} from '@/types/api-client/super-admin-permissions'

// Super Admin Permissions API helper
export function createSuperAdminPermissionsApi(
	request: <T>(endpoint: string, options?: ExtendedFetchOptions) => Promise<T | null>,
	createApiState: <T>(endpoint: string, options?: FetchOptions) => ApiState<T>
) {
	return {
		/**
		 * Get single permission
		 * @param permissionId
		 * @param options
		 * @returns Promise<SuperAdminPermission>
		 * @example
		 * ``` typescript
		 * await api.superAdminPermissions.getOne(1)
		 * ```
		 */
		getOne: (permissionId: number, options: ExtendedFetchOptions = {}) =>
			request<SuperAdminPermission>(`/sadmin/permissions/${permissionId}`, options),

		/**
		 * Create permission
		 * @param payload
		 * @param options
		 * @returns Promise<SuperAdminPermission>
		 * @example
		 * ``` typescript
		 * await api.superAdminPermissions.create({ name: 'New Permission' })
		 * ```
		 */
		create: (payload: CreatePermissionPayload, options: ExtendedFetchOptions = {}) => {
			return request<SuperAdminPermission>('/sadmin/permissions', {
				method: 'POST',
				body: payload,
				...options
			})
		},

		/**
		 * Update permission
		 * @param permissionId route param
		 * @param payload body params
		 * @param options optional fetch config
		 * @returns Promise<SuperAdminPermission>
		 * @example
		 * ``` typescript
		 * await api.superAdminPermissions.update(1, { name: 'Updated Permission' })
		 * ```
		 */
		update: (permissionId: number, payload: UpdatePermissionPayload, options: ExtendedFetchOptions = {}) => {
			return request<SuperAdminPermission>(`/sadmin/permissions/${permissionId}`, {
				method: 'PUT',
				body: payload,
				...options
			})
		},

		/**
		 * Delete permission
		 * @param permissionId route param
		 * @param options optional fetch config
		 * @returns Promise<ApiSuccessResponse>
		 * @example
		 * ``` typescript
		 * await api.superAdminPermissions.delete(1)
		 * ```
		 */
		delete: (permissionId: number, options: ExtendedFetchOptions = {}) =>
			request<ApiSuccessResponse>(`/sadmin/permissions/${permissionId}`, {
				method: 'DELETE',
				...options
			}),

		/**
		 * Assign permissions to user
		 * @param payload body params
		 * @param options optional fetch config
		 * @returns Promise<ApiSuccessResponse>
		 * @example
		 * ``` typescript
		 * await api.superAdminPermissions.assignToUser({ userId: 1, permissionIds: [2, 3] })
		 * ```
		 */
		assignToUser: (payload: AssignPermissionPayload, options: ExtendedFetchOptions = {}) => {
			return request<ApiSuccessResponse>('/sadmin/permissions/assign-to-user', {
				method: 'POST',
				body: payload,
				...options
			})
		},

		/**
		 * Remove permissions from user
		 * @param payload body params
		 * @param options optional fetch config
		 * @returns Promise<ApiSuccessResponse>
		 * @example
		 * ``` typescript
		 * await api.superAdminPermissions.removeFromUser({ userId: 1, permissionIds: [2, 3] })
		 * ```
		 */
		removeFromUser: (payload: RemovePermissionPayload, options: ExtendedFetchOptions = {}) => {
			return request<ApiSuccessResponse>('/sadmin/permissions/remove-from-user', {
				method: 'POST',
				body: payload,
				...options
			})
		},

		// Reactive state methods (destructurable)
		/**
		 * Get all permissions (return reactive)
		 * @param options optional fetch config
		 * @returns Promise<ApiState<SuperAdminPermission[]>>
		 * @example
		 * ``` typescript
		 * const { data: permissions, refresh, isLoading, error } = api.superAdminPermissions.useGetAll()
		 * ```
		 */
		useGetAll: (options: FetchOptions = {}): ApiState<SuperAdminPermission[]> =>
			createApiState<SuperAdminPermission[]>('/sadmin/permissions', options)
	}
}
